import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// Thunk: Fetch orders based on type
export const fetchOrders = createAsyncThunk(
  "orders/fetchOrders",
  async ({ status = "all", page = 1, agent, page_size = 20 }, { rejectWithValue }) => { 
    try {
      const response = await axios.get(`${apiUrl}/AllOrders/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        params: { Status: status, page, agent, page_size }, 
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch orders");
    }
  }
);

// Thunk: Fetch an order by ID
export const fetchOrderById = createAsyncThunk(
  "orders/fetchOrderById",
  async (order_id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/GetOrder/${order_id}/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      return response.data; // Return the fetched order data
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch order");
    }
  }
);

// Thunk: Update order status
export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async ({ order_id, status, replay }, { rejectWithValue }) => {
    try {
      // بناء الـ request body فقط مع الـ fields المطلوبة
      const requestBody = {};
      
      // إضافة status (مطلوب دائماً)
      if (status) {
        requestBody.status = String(status); // تأكد إنه string
      }
      
      // إضافة replay فقط إذا كان موجود وليس فارغ
      if (replay && String(replay).trim() !== "") {
        requestBody.replay = String(replay).trim(); // تأكد إنه string
      }

      console.log("=== API Request Details ===");
      console.log("URL:", `${apiUrl}/GetOrder/${order_id}/UpdateStatus/`);
      console.log("Method: PATCH");
      console.log("Request Body:", JSON.stringify(requestBody, null, 2));
      console.log("Headers:", {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem('accessToken')?.substring(0, 20)}...`
      });

      const response = await axios.patch(
        `${apiUrl}/GetOrder/${order_id}/UpdateStatus/`,
        requestBody, // إرسال object بسيط
        {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      
      console.log("=== API Response ===");
      console.log("Status:", response.status);
      console.log("Response Data:", response.data);
      
      return response.data;
    } catch (error) {
      console.error("=== API Error ===");
      console.error("Error Status:", error.response?.status);
      console.error("Error Data:", error.response?.data);
      console.error("Full Error:", error);
      return rejectWithValue(error.response?.data || "Failed to update order status");
    }
  }
);

// Async Thunk for searching orders
export const searchOrder = createAsyncThunk(
  "orders/searchOrder",
  async ({ query = "", search_field = "transaction_id", page = 1 }, { rejectWithValue }) => {
    try {
      // ✅ بناء الـ params ديناميكياً
      const params = {
        [search_field]: query, // ✅ استخدام الـ field مباشرة
        page
      };

      const response = await axios.get(`${apiUrl}/GetOrder/search/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        params: params, // ✅ مثال: { transaction_id: "DC-12345", page: 1 }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to search orders");
    }
  }
);


// Thunk: Check SMS messages for Daily Sim orders
export const checkSMSMessages = createAsyncThunk(
  "orders/checkSMSMessages",
  async (transaction_id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/DailySim/Check/${transaction_id}/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      return response.data; // Return the SMS data
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to check SMS messages");
    }
  }
);

// Thunk: Fetch retry pending orders
export const fetchRetryPendingOrders = createAsyncThunk(
  "orders/fetchRetryPendingOrders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/Orders/RetryPending/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch retry pending orders");
    }
  }
);

// Thunk: Retry pending order
export const retryPendingOrder = createAsyncThunk(
  "orders/retryPendingOrder",
  async (order_id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/Orders/RetryPending/`,
        { order_id },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to retry order");
    }
  }
);

// Thunk: Fetch Daily Card pending count
export const fetchDailyCardPendingCount = createAsyncThunk(
  "orders/fetchDailyCardPendingCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/AllOrders/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        params: { Status: "pending", page: 1, agent: "Daily Card", page_size: 100 },
      });
      
      // استخراج العدد الإجمالي من الـ response
      return response.data?.count || 0;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch Daily Card count");
    }
  }
);

// Thunk: Fetch Product Details by ID
export const fetchProductById = createAsyncThunk(
  "orders/fetchProductById",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiUrl}/Products/${productId}/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch product");
    }
  }
);



// init Slice
const ordersSlice = createSlice({
  name: "orders",
  initialState: {
    orders: [],
    currentOrder: null, // State for the current order by ID
    searchResults: null,
    smsMessages: null, // New state for SMS messages
    retryPendingOrders: null,
    dailyCardPendingCount: 0,
    currentProduct: null,
    
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    clearSMSMessages: (state) => {
      state.smsMessages = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = null;
    },
    clearRetryPendingOrders: (state) => {
      state.retryPendingOrders = null;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.orders = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch orders";
      })
      .addCase(fetchOrderById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch order";
      })
      .addCase(updateOrderStatus.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.orders = action.payload;
        })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to update order status";
      })
      .addCase(searchOrder.fulfilled, (state, action) => {
        state.status = "succeeded";
      
  
  const currentPage = action.meta.arg.page || 1;
  
  // ✅ إذا كانت الصفحة الأولى أو مفيش نتائج سابقة
  if (currentPage === 1 || !state.searchResults) {
    state.searchResults = action.payload;
  } else {
    // ✅ تأكد إن الصفحة الجديدة فعلاً مختلفة
    const existingOrders = state.searchResults?.orders || [];
    const newOrders = action.payload?.orders || [];
    
    // ✅ فلتر الطلبات المكررة
    const uniqueNewOrders = newOrders.filter(
      newOrder => !existingOrders.some(
        existingOrder => existingOrder.order_id === newOrder.order_id
      )
    );
    
    state.searchResults = {
      ...action.payload,
      orders: [...existingOrders, ...uniqueNewOrders]
    };
  }
      })
      .addCase(searchOrder.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchOrder.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(checkSMSMessages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(checkSMSMessages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.smsMessages = action.payload;
      })
      .addCase(checkSMSMessages.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to check SMS messages";
      })
      .addCase(fetchRetryPendingOrders.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchRetryPendingOrders.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.retryPendingOrders = action.payload;
        state.retryPendingCount = action.payload?.count || 0;
      })
      .addCase(fetchRetryPendingOrders.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch retry pending orders";
      })
      .addCase(retryPendingOrder.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(retryPendingOrder.fulfilled, (state, action) => {
        state.status = "succeeded";
        // يمكن تحديث القائمة أو إزالة الطلب من القائمة
      })
      .addCase(retryPendingOrder.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to retry order";
      })
      .addCase(fetchDailyCardPendingCount.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchDailyCardPendingCount.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.dailyCardPendingCount = action.payload;
      })
      .addCase(fetchDailyCardPendingCount.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch Daily Card count";
      })
      .addCase(fetchProductById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch product";
      });
  },
});

// Export the action creators
export const { 
  clearSMSMessages, 
  clearSearchResults, 
  clearRetryPendingOrders,
  clearCurrentProduct 
} = ordersSlice.actions;
export default ordersSlice.reducer;